import { useDeferredValue, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import { Search, X } from 'lucide-react';
import type { TrialListQuery } from '@ctms/contracts';

import { trialsApi } from '../../trials/trials.api.js';
import { trialQueryKeys } from '../../trials/trials.query-keys.js';

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

export interface TrialSearchDialogProps {
  isOpen: boolean;
  /** A picked trial becomes the active one (the page saves the recall itself). */
  onSelect: (trialId: number) => void;
  onClose: () => void;
}

/**
 * The loupe (#7 A6, frozen feature trial-bars-and-search.feature): an
 * ALPHABETICAL list of ALL active trials of the site, narrowed while typing
 * one phrase matched against skrót / nazwa / numer ośrodka (server search —
 * the site-number matching landed with #7 in `trialFilters`). A result row
 * presents the shortcut, the name and the site number; picking one sets the
 * active trial and closes the window. Inactive trials never appear
 * (REQ-TRIAL-002 — the server filter `active: true`).
 *
 * Specialization is the fourth search field ONLY after Q-TRIAL-007 is
 * decided (@todo scenario) — deliberately absent here.
 *
 * The query key shape matches the ActiveTrialPicker pattern, so an empty
 * phrase reuses the picker's cached first page. `useDeferredValue` keeps
 * typing responsive; the fetch follows the deferred phrase.
 */
export function TrialSearchDialog({
  isOpen,
  onSelect,
  onClose,
}: TrialSearchDialogProps): JSX.Element | null {
  const [phrase, setPhrase] = useState('');
  const deferredPhrase = useDeferredValue(phrase);

  const query: TrialListQuery = {
    page: 1,
    limit: 100,
    active: true,
    ...(deferredPhrase.trim() !== '' ? { search: deferredPhrase.trim() } : {}),
  };
  const trialsQuery = useQuery({
    queryKey: trialQueryKeys.list(query),
    queryFn: () => trialsApi.listTrials(query),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  if (!isOpen) {
    return null;
  }

  // Alphabetical per the frozen scenario ("ułożoną alfabetycznie") — by the
  // shortcut, the label the coordinator recognizes at a glance.
  const trials = [...(trialsQuery.data?.items ?? [])].sort((a, b) =>
    a.shortcut.localeCompare(b.shortcut),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-xs">
      <div className="w-full max-w-xl rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <h2 id="trial-search-title" className="flex items-center gap-2 text-base font-semibold">
            <Search className="h-4 w-4 text-cyan-400" />
            Szukaj badania
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <input
          type="text"
          value={phrase}
          onChange={(event) => setPhrase(event.target.value)}
          placeholder="Skrót, nazwa lub numer ośrodka…"
          aria-label="Szukana fraza"
          data-testid="trial-search-input"
          autoFocus
          className="mt-4 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
        />

        {trialsQuery.isError ? (
          <p className="mt-4 text-sm text-rose-400">
            Nie udało się wczytać listy badań. Spróbuj ponownie.
          </p>
        ) : (
          <ul
            data-testid="trial-search-results"
            aria-labelledby="trial-search-title"
            className="mt-3 max-h-96 overflow-y-auto"
          >
            {trials.map((trial) => (
              <li key={trial.id}>
                <button
                  type="button"
                  data-testid={`trial-search-result-${trial.id}`}
                  onClick={() => {
                    onSelect(trial.id);
                    onClose();
                  }}
                  className="w-full rounded-md px-3 py-2 text-left hover:bg-slate-800"
                >
                  <span className="text-sm font-bold text-cyan-300">{trial.shortcut}</span>
                  <span className="ml-2 text-sm text-slate-100">{trial.shortTitle}</span>
                  <span className="ml-2 text-xs text-slate-400">ośrodek {trial.siteNumber}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {!trialsQuery.isError && !trialsQuery.isLoading && trials.length === 0 ? (
          <p className="mt-3 text-sm text-slate-400">Brak badań spełniających kryteria.</p>
        ) : null}
        <p className="mt-3 text-[11px] text-slate-500">
          Lista prezentuje wyłącznie badania aktywne, alfabetycznie po skrócie.
        </p>
      </div>
    </div>
  );
}
